package com.elan.edubridge.data.firebase

import com.elan.edubridge.data.firebase.externals.Firestore
import com.elan.edubridge.data.firebase.externals.Functions
import com.elan.edubridge.data.firebase.externals.doc
import com.elan.edubridge.data.firebase.externals.getDoc
import com.elan.edubridge.data.firebase.externals.httpsCallable
import com.elan.edubridge.shared.data.datasource.AcademyCommandDataSource
import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.domain.model.AcademyApplicationRequest
import com.elan.edubridge.shared.domain.model.AcceptInvitationResult
import com.elan.edubridge.shared.domain.model.AmendAttendanceRequest
import com.elan.edubridge.shared.domain.model.AttachmentContentType
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.BatchCommandResult
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.CalendarEventResult
import com.elan.edubridge.shared.domain.model.ClassSchedule
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CommandResult
import com.elan.edubridge.shared.domain.model.CommentResult
import com.elan.edubridge.shared.domain.model.ContentReportRequest
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ContentReportResult
import com.elan.edubridge.shared.domain.model.ContentReportStatus
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.ContentStatus
import com.elan.edubridge.shared.domain.model.CreateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.CreateClassRequest
import com.elan.edubridge.shared.domain.model.CreateCommentRequest
import com.elan.edubridge.shared.domain.model.CreateInvitationRequest
import com.elan.edubridge.shared.domain.model.AssignmentAttachmentTicket
import com.elan.edubridge.shared.domain.model.CreateAssignmentRequest
import com.elan.edubridge.shared.domain.model.CreateNoticeRequest
import com.elan.edubridge.shared.domain.model.CreatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.CreatePostAttachmentUploadRequest
import com.elan.edubridge.shared.domain.model.CreatePostRequest
import com.elan.edubridge.shared.domain.model.CreateStudentRequest
import com.elan.edubridge.shared.domain.model.EventScope
import com.elan.edubridge.shared.domain.model.GuardianContact
import com.elan.edubridge.shared.domain.model.InvitationResult
import com.elan.edubridge.shared.domain.model.InvitationPreviewResult
import com.elan.edubridge.shared.domain.model.InvitationPreviewStatus
import com.elan.edubridge.shared.domain.model.InviteeType
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.JoinCodePreviewResult
import com.elan.edubridge.shared.domain.model.JoinCodePreviewStatus
import com.elan.edubridge.shared.domain.model.JoinCodeResult
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.NoticeResult
import com.elan.edubridge.shared.domain.model.NotificationSummary
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.PostAttachmentDownloadTicket
import com.elan.edubridge.shared.domain.model.PostAttachmentUploadTicket
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.PostResult
import com.elan.edubridge.shared.domain.model.QrSession
import com.elan.edubridge.shared.domain.model.QrSessionResult
import com.elan.edubridge.shared.domain.model.QrSessionStatus
import com.elan.edubridge.shared.domain.model.QrToken
import com.elan.edubridge.shared.domain.model.QrTokenResult
import com.elan.edubridge.shared.domain.model.RecordAttendanceRequest
import com.elan.edubridge.shared.domain.model.ScanRejection
import com.elan.edubridge.shared.domain.model.ScanRequest
import com.elan.edubridge.shared.domain.model.ScanResult
import com.elan.edubridge.shared.domain.model.SubmitJoinApplicationResult
import com.elan.edubridge.shared.domain.model.UpdateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.UpdateAcademyRequest
import com.elan.edubridge.shared.domain.model.UpdateAssignmentRequest
import com.elan.edubridge.shared.domain.model.UpdateClassRequest
import com.elan.edubridge.shared.domain.model.UpdateNoticeRequest
import com.elan.edubridge.shared.domain.model.UpdatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.UpdatePostRequest
import com.elan.edubridge.shared.domain.model.UpdateStudentRequest
import com.elan.edubridge.shared.domain.model.UserRole
import kotlin.js.json
import kotlinx.coroutines.await

/** 학원 설정에 담당 선생님이 배정 안 됐을 때 회전 주기 기본값(§S1 `AcademyData.qrRotationSeconds`). */
private const val DEFAULT_QR_ROTATION_SECONDS = 20

// jsTypeOf/stringOrNull/intOrDefault는 Dto.kt의 internal 함수를 그대로 쓴다(같은 패키지, import 불필요).

/**
 * [AcademyCommandDataSource]의 Firebase Functions 콜러블 구현. 15종 전부 `edubridge-<함수명>` +
 * asia-northeast3로 나간다(`FirebaseNaming.FUNCTIONS_PREFIX`/`FUNCTIONS_REGION`, `Functions` 인스턴스
 * 생성 시점에 이미 리전이 고정되어 있다 — `WebGraph.kt` 참고).
 *
 * **학원 개설 신청 입력**: `AcademyApplicationRequest`의 `academyName`/`ownerName`/`phone`/`address`/
 * `description`은 `functions/src/roles.ts`의 `submitAcademyApplication` 입력과 1:1로 전송한다.
 *
 * **함정 2(식별자 불일치)**: `AmendAttendanceRequest.previousEventId`는 이벤트 id이지만
 * `functions/src/attendance.ts`의 `amendAttendance`는 `attendanceRecordId`(레코드 id)를 받는다.
 * [amendAttendance]가 이벤트 문서의 `recordId` 필드를 먼저 읽어 다리를 놓는다.
 *
 * **함정 3(미구현 콜러블)**: QR 4종 + `notificationSummary`는 S3가 이 lane과 병렬로 만드는 중이라
 * 이 파일 작성 시점에는 실제 배포가 없다. 응답 필드는 계획서 §3.3 표를 근거로 방어적으로
 * (`stringOrNull`/`intOrDefault` 기본값) 파싱한다 — S3 완료 후 실제 응답과 대조 검증이 필요하다.
 * `scanAttendance` 거부 사유만은 S3가 계약을 확정해 줬다 — [scanRejectionFor] 참고.
 */
class FirebaseAcademyCommandDataSource(
    private val functions: Functions,
    private val firestore: Firestore,
) : AcademyCommandDataSource {

    // ---- 프로비저닝 ----

    override suspend fun submitAcademyApplication(request: AcademyApplicationRequest): CommandResult = invokeCommand(
        "submitAcademyApplication",
        json(
            "academyName" to request.academyName,
            "contactPhone" to request.phone,
            "ownerName" to request.ownerName,
            "address" to request.address,
            "description" to request.description,
        ),
    ) { stringOrNull(it, "applicationId") }

    override suspend fun approveAcademyApplication(applicationId: String): CommandResult = invokeCommand(
        "approveAcademyApplication",
        json("applicationId" to applicationId),
    ) { stringOrNull(it, "academyId") }

    override suspend fun updateAcademy(request: UpdateAcademyRequest): CommandResult = invokeCommand(
        "updateAcademy",
        json(
            "academyId" to request.academyId,
            "name" to request.name,
            "address" to request.address,
            "phone" to request.phone,
            "description" to request.description,
        ),
    ) { stringOrNull(it, "academyId") }

    override suspend fun createClass(request: CreateClassRequest): CommandResult = invokeCommand(
        "createClass",
        json(
            "academyId" to request.academyId,
            "name" to request.className,
            "subject" to request.subject,
            "teacherUserId" to request.teacherUserId,
            "schedule" to scheduleJson(request.schedule),
        ),
    ) { stringOrNull(it, "classId") }

    override suspend fun updateClass(request: UpdateClassRequest): CommandResult = invokeCommand(
        "updateClass",
        json(
            "academyId" to request.academyId,
            "classId" to request.classId,
            "name" to request.className,
            "subject" to request.subject,
            "schedule" to scheduleJson(request.schedule),
        ),
    ) { stringOrNull(it, "classId") }

    override suspend fun createStudent(request: CreateStudentRequest): CommandResult = invokeCommand(
        "createStudent",
        json(
            "academyId" to request.academyId,
            "name" to request.name,
            "grade" to request.grade,
            "phone" to request.phone,
            "guardianContacts" to guardianContactsJson(request.guardianContacts),
        ),
    ) { stringOrNull(it, "studentId") }

    override suspend fun updateStudent(request: UpdateStudentRequest): CommandResult = invokeCommand(
        "updateStudent",
        json(
            "academyId" to request.academyId,
            "studentId" to request.studentId,
            "name" to request.name,
            "grade" to request.grade,
            "phone" to request.phone,
            "guardianContacts" to guardianContactsJson(request.guardianContacts),
        ),
    ) { stringOrNull(it, "studentId") }

    override suspend fun createEnrollment(
        academyId: String,
        classId: String,
        studentId: String,
    ): CommandResult = invokeCommand(
        "createEnrollment",
        json("academyId" to academyId, "classId" to classId, "studentId" to studentId),
    ) { stringOrNull(it, "enrollmentId") }

    // ---- 초대 ----

    /**
     * 선택 필드는 값이 있을 때만 payload에 넣는다. 서버가 `expiresInHours`의 부재를
     * `undefined`로만 판정해 기본값(72시간)을 채우므로(`invitations.ts`), 명시적 null을 보내면
     * `requireInt`에 걸려 `invalid-argument`가 된다.
     */
    override suspend fun createInvitation(request: CreateInvitationRequest): InvitationResult = try {
        val payload = json(
            "academyId" to request.academyId,
            "inviteeType" to request.inviteeType.name,
        )
        request.targetId?.let { payload["targetId"] = it }
        request.relation?.let { payload["relation"] = it }
        request.expiresInHours?.let { payload["expiresInHours"] = it }
        val response = httpsCallable(functions, callableName("createInvitation"))(payload).await()
        val data = response.data
        val code = stringOrNull(data, "code")
        if (code == null) {
            InvitationResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            InvitationResult(
                code = code,
                academyId = stringOrNull(data, "academyId"),
                inviteeType = stringOrNull(data, "inviteeType").toInviteeTypeOrNull(),
                targetId = stringOrNull(data, "targetId"),
                expiresAt = stringOrNull(data, "expiresAt"),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.createInvitation failed", e)
        InvitationResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    /**
     * 로그인한 사람에게만 비소비 확인 결과를 준다. 웹 `/invite` 착지는 코드를 읽지 않고 앱 설치만
     * 안내하므로, 이 어댑터는 향후 앱과 같은 공유 흐름을 쓸 때만 호출된다.
     */
    override suspend fun previewInvitation(invitationCode: String): InvitationPreviewResult = try {
        val response = httpsCallable(functions, callableName("previewInvitation"))(
            json("invitationCode" to invitationCode),
        ).await()
        val data = response.data
        val status = InvitationPreviewStatus.entries.firstOrNull {
                it.name == stringOrNull(data, "status")
            } ?: InvitationPreviewStatus.UNUSABLE
        val containsTarget = status == InvitationPreviewStatus.USABLE ||
            status == InvitationPreviewStatus.ALREADY_ACCEPTED_BY_CALLER
        InvitationPreviewResult(
            status = status,
            academyId = if (containsTarget) stringOrNull(data, "academyId") else null,
            academyName = if (containsTarget) stringOrNull(data, "academyName") else null,
            inviteeType = if (containsTarget) stringOrNull(data, "inviteeType").toInviteeTypeOrNull() else null,
            expiresAt = if (containsTarget) stringOrNull(data, "expiresAt") else null,
        )
    } catch (e: Throwable) {
        // A preview code is a bearer capability. Keep diagnostics to the callable error code rather
        // than handing an implementation-specific error object to the browser console.
        console.error("[EduBridge] callable.previewInvitation failed", e.jsErrorCode())
        InvitationPreviewResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    /** 보호자 수락이면 `role`·`permission`이 서버에서 null로 온다 — 그대로 null을 유지한다(§2.6). */
    override suspend fun acceptInvitation(invitationCode: String): AcceptInvitationResult = try {
        val response = httpsCallable(functions, callableName("acceptInvitation"))(
            json("invitationCode" to invitationCode),
        ).await()
        val data = response.data
        AcceptInvitationResult(
            academyId = stringOrNull(data, "academyId"),
            academyName = stringOrNull(data, "academyName"),
            inviteeType = stringOrNull(data, "inviteeType").toInviteeTypeOrNull(),
            role = stringOrNull(data, "role")?.let { raw -> UserRole.entries.firstOrNull { it.name == raw } },
            permission = stringOrNull(data, "permission")?.let { raw -> Permission.entries.firstOrNull { it.name == raw } },
            studentId = stringOrNull(data, "studentId"),
            guardianLinkId = stringOrNull(data, "guardianLinkId"),
            replayed = jsTypeOf(data["replayed"]) == "boolean" && data["replayed"] as Boolean,
        )
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.acceptInvitation failed", e)
        AcceptInvitationResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun assignTeacherToClass(
        academyId: String,
        classId: String,
        teacherUserId: String,
    ): CommandResult = invokeCommand(
        "assignTeacherToClass",
        json("academyId" to academyId, "classId" to classId, "teacherUserId" to teacherUserId),
    ) { stringOrNull(it, "classId") }

    /**
     * 관찰자 관계 해지(§4.4 정리 트리거의 유일한 방아쇠). 호출자는 학원 ADMIN 또는 보호자 본인 —
     * [linkId]는 [com.elan.edubridge.shared.domain.model.GuardianLink.linkId](계산 프로퍼티,
     * `{studentId}_{guardianUserId}`)에서 얻는다.
     */
    override suspend fun revokeGuardianLink(academyId: String, linkId: String): CommandResult = invokeCommand(
        "revokeGuardianLink",
        json("academyId" to academyId, "linkId" to linkId),
    ) { linkId }

    // ---- 출결 ----

    override suspend fun recordAttendance(request: RecordAttendanceRequest): CommandResult = invokeCommand(
        "recordAttendance",
        json(
            "academyId" to request.academyId,
            "classId" to request.classId,
            "studentId" to request.studentId,
            "serviceDate" to request.serviceDate,
            "status" to request.status.name,
            "idempotencyKey" to request.idempotencyKey,
        ),
    ) { stringOrNull(it, "eventId") }

    /**
     * v4 `출석 처리`/`2명 출석 처리`. `functions/src/attendance.ts`의 콜러블은 `{academyId,
     * serviceDate, classId?, entries}` 형태(공통 academyId/serviceDate + 건별 entries)를 받는다 —
     * 요청 전체가 같은 academyId·serviceDate를 공유한다고 가정하고 첫 항목에서 취한다. 부분 성공은
     * 서버가 개별 처리해 `results[].ok`로 돌려준다([toBatchEntryResults]).
     */
    override suspend fun recordAttendanceBatch(requests: List<RecordAttendanceRequest>): BatchCommandResult {
        if (requests.isEmpty()) return BatchCommandResult()
        val first = requests.first()
        val payload = json(
            "academyId" to first.academyId,
            "serviceDate" to first.serviceDate,
            "entries" to requests.map {
                json(
                    "studentId" to it.studentId,
                    "status" to it.status.name,
                    "idempotencyKey" to it.idempotencyKey,
                    "classId" to it.classId,
                )
            }.toTypedArray(),
        )
        return try {
            val response = httpsCallable(functions, callableName("recordAttendanceBatch"))(payload).await()
            BatchCommandResult(results = toBatchEntryResults(response.data))
        } catch (e: Throwable) {
            console.error("[EduBridge] callable.recordAttendanceBatch failed", e)
            BatchCommandResult(
                failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
                errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
            )
        }
    }

    override suspend fun amendAttendance(request: AmendAttendanceRequest): CommandResult {
        val recordId = resolveAttendanceRecordId(request.academyId, request.previousEventId)
            ?: return CommandResult(
                failure = CommandFailure.NOT_FOUND,
                errorMessage = "정정할 출결 기록을 찾을 수 없어요.",
            )

        return invokeCommand(
            "amendAttendance",
            json(
                "academyId" to request.academyId,
                "attendanceRecordId" to recordId,
                "status" to request.status.name,
                "reason" to request.reason,
                "idempotencyKey" to request.idempotencyKey,
            ),
        ) { stringOrNull(it, "eventId") }
    }

    // ---- QR 4종 ----

    override suspend fun createQrSession(academyId: String, label: String): QrSessionResult = try {
        val response = httpsCallable(functions, callableName("createQrSession"))(
            json("academyId" to academyId, "label" to label),
        ).await()
        val data = response.data
        val sessionId = stringOrNull(data, "sessionId")
        val nonce = stringOrNull(data, "nonce")
        val expiresAt = stringOrNull(data, "expiresAt")
        if (sessionId == null || nonce == null || expiresAt == null) {
            QrSessionResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            val rotationSeconds = intOrDefault(data, "rotationSeconds", DEFAULT_QR_ROTATION_SECONDS)
            QrSessionResult(
                session = QrSession(
                    sessionId = sessionId,
                    academyId = academyId,
                    label = label,
                    status = QrSessionStatus.ACTIVE,
                    rotationSeconds = rotationSeconds,
                ),
                token = QrToken(nonce = nonce, expiresAt = expiresAt, rotationSeconds = rotationSeconds),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.createQrSession failed", e)
        QrSessionResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun rotateQrToken(academyId: String, sessionId: String): QrTokenResult = try {
        val response = httpsCallable(functions, callableName("rotateQrToken"))(
            json("academyId" to academyId, "sessionId" to sessionId),
        ).await()
        val data = response.data
        val nonce = stringOrNull(data, "nonce")
        val expiresAt = stringOrNull(data, "expiresAt")
        if (nonce == null || expiresAt == null) {
            QrTokenResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            QrTokenResult(
                token = QrToken(
                    nonce = nonce,
                    expiresAt = expiresAt,
                    rotationSeconds = intOrDefault(data, "rotationSeconds", DEFAULT_QR_ROTATION_SECONDS),
                ),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.rotateQrToken failed", e)
        QrTokenResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun scanAttendance(request: ScanRequest): ScanResult = try {
        val response = httpsCallable(functions, callableName("scanAttendance"))(
            json(
                "academyId" to request.academyId,
                "sessionId" to request.sessionId,
                "nonce" to request.nonce,
                "clientScannedAt" to request.clientScannedAt,
            ),
        ).await()
        val data = response.data
        // 멱등 재시도가 서버의 기존 성공 결과를 그대로 돌려받은 경우(§3.4-5) — 두 번째 "출석
        // 처리됐어요" 화면을 또 띄우면 사용자가 방금 다시 스캔이 반영된 줄 착각한다(리뷰 B7).
        val replayed = jsTypeOf(data["replayed"]) == "boolean" && data["replayed"] as Boolean
        ScanResult(
            eventId = stringOrNull(data, "eventId"),
            status = stringOrNull(data, "status")?.let { raw -> AttendanceStatus.entries.firstOrNull { it.name == raw } },
            studentName = stringOrNull(data, "studentName"),
            className = stringOrNull(data, "className"),
            occurredAt = stringOrNull(data, "occurredAt"),
            message = if (replayed) "이미 처리된 스캔이에요." else stringOrNull(data, "message"),
        )
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.scanAttendance failed", e)
        val rejection = scanRejectionFor(e)
        ScanResult(
            rejection = rejection,
            message = scanRejectionMessage(rejection) ?: e.jsErrorMessageOrDefault("QR 코드를 다시 확인해 주세요."),
        )
    }

    /**
     * [ScanRejection.SESSION_NOT_FOUND]·[ScanRejection.NO_STUDENT_RECORD]는 서버 원문 메시지 대신
     * 고정 한국어 문구를 쓴다(리뷰 B7, MVP §9 "공급자 코드가 아닌 사용자가 해결할 수 있는 문장").
     * 나머지 사유는 기존처럼 서버 메시지/기본 문구로 폴백한다(null 반환).
     */
    private fun scanRejectionMessage(rejection: ScanRejection): String? = when (rejection) {
        ScanRejection.SESSION_NOT_FOUND -> "이 QR은 더 이상 사용되지 않아요. 데스크에 문의해 주세요."
        ScanRejection.NO_STUDENT_RECORD -> "계정과 학생 정보 연결이 필요해요. 선생님께 문의해 주세요."
        else -> null
    }

    override suspend fun endQrSession(academyId: String, sessionId: String): CommandResult = invokeCommand(
        "endQrSession",
        json("academyId" to academyId, "sessionId" to sessionId),
    ) { sessionId }

    // ---- 공지 ----

    override suspend fun createNotice(request: CreateNoticeRequest): NoticeResult = try {
        val payload = json(
            "academyId" to request.academyId,
            "title" to request.title,
            "content" to request.content,
        )
        request.contentHtml?.let { payload["contentHtml"] = it }
        val response = httpsCallable(functions, callableName("createNotice"))(payload).await()
        val data = response.data
        val noticeId = stringOrNull(data, "noticeId")
        if (noticeId == null) {
            NoticeResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            NoticeResult(
                notice = NoticeData(
                    id = noticeId,
                    academyId = stringOrNull(data, "academyId") ?: request.academyId,
                    title = stringOrNull(data, "title") ?: request.title,
                    date = stringOrNull(data, "date") ?: "",
                    content = stringOrNull(data, "content") ?: request.content,
                    contentHtml = stringOrNull(data, "contentHtml"),
                    status = stringOrNull(data, "status").toContentStatus(),
                    authorUid = stringOrNull(data, "authorUid"),
                    authorName = stringOrNull(data, "authorName"),
                ),
                notified = pushDeliverySummaryOrNull(data, "notified"),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.createNotice failed", e)
        NoticeResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    /** 서버는 `undefined`인 필드만 "변경 없음"으로 본다 — null을 보내면 `invalid-argument`다. */
    override suspend fun updateNotice(request: UpdateNoticeRequest): CommandResult {
        val payload = json("academyId" to request.academyId, "noticeId" to request.noticeId)
        request.title?.let { payload["title"] = it }
        request.content?.let { payload["content"] = it }
        request.contentHtml?.let { payload["contentHtml"] = it }
        return invokeCommand("updateNotice", payload) { stringOrNull(it, "noticeId") }
    }

    override suspend fun deleteNotice(academyId: String, noticeId: String): CommandResult = invokeCommand(
        "deleteNotice",
        json("academyId" to academyId, "noticeId" to noticeId),
    ) { stringOrNull(it, "noticeId") }

    // ---- 과제 ----

    /** `combinationKey`는 보내지 않는다 — EduMint 문제 은행의 키라서 서버가 조합한다. */
    override suspend fun createAssignment(request: CreateAssignmentRequest): CommandResult {
        val payload = json(
            "academyId" to request.academyId,
            "title" to request.title,
            "dueDate" to request.dueDate,
            "kind" to request.kind.name,
        )
        request.classId?.let { payload["classId"] = it }
        request.problemBank?.let { selection ->
            payload["problemBank"] = json(
                "subject" to selection.subject,
                "grade" to selection.grade,
                "difficulty" to selection.difficulty,
                "questionType" to selection.questionType,
                "questionCount" to selection.questionCount,
            )
        }
        return invokeCommand("createAssignment", payload) { stringOrNull(it, "assignmentId") }
    }

    /** 서버는 `undefined`인 필드만 "변경 없음"으로 본다 — null을 보내면 `invalid-argument`다. */
    override suspend fun updateAssignment(request: UpdateAssignmentRequest): CommandResult {
        val payload = json("academyId" to request.academyId, "assignmentId" to request.assignmentId)
        request.title?.let { payload["title"] = it }
        request.dueDate?.let { payload["dueDate"] = it }
        return invokeCommand("updateAssignment", payload) { stringOrNull(it, "assignmentId") }
    }

    override suspend fun deleteAssignment(academyId: String, assignmentId: String): CommandResult = invokeCommand(
        "deleteAssignment",
        json("academyId" to academyId, "assignmentId" to assignmentId),
    ) { stringOrNull(it, "assignmentId") }

    /** 파일은 이미 Storage 스테이징에 올라가 있다 — 이 호출은 그것을 과제로 승격시킬 뿐이다. */
    override suspend fun attachAssignmentUpload(
        academyId: String,
        assignmentId: String,
        uploadId: String,
        fileName: String,
    ): CommandResult = invokeCommand(
        "attachAssignmentUpload",
        json(
            "academyId" to academyId,
            "assignmentId" to assignmentId,
            "uploadId" to uploadId,
            "fileName" to fileName,
        ),
    ) { stringOrNull(it, "attachmentId") }

    override suspend fun removeAssignmentAttachment(
        academyId: String,
        assignmentId: String,
        attachmentId: String,
    ): CommandResult = invokeCommand(
        "removeAssignmentAttachment",
        json(
            "academyId" to academyId,
            "assignmentId" to assignmentId,
            "attachmentId" to attachmentId,
        ),
    ) { stringOrNull(it, "attachmentId") }

    override suspend fun getAssignmentAttachmentUrl(
        academyId: String,
        assignmentId: String,
        attachmentId: String,
    ): AssignmentAttachmentTicket = try {
        val response = httpsCallable(functions, callableName("getAssignmentAttachmentUrl"))(
            json(
                "academyId" to academyId,
                "assignmentId" to assignmentId,
                "attachmentId" to attachmentId,
            ),
        ).await()
        val data = response.data
        val downloadUrl = stringOrNull(data, "downloadUrl")
        if (downloadUrl == null) {
            AssignmentAttachmentTicket(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            AssignmentAttachmentTicket(
                attachmentId = stringOrNull(data, "attachmentId") ?: attachmentId,
                academyId = stringOrNull(data, "academyId") ?: academyId,
                assignmentId = stringOrNull(data, "assignmentId") ?: assignmentId,
                fileName = stringOrNull(data, "fileName"),
                contentType = AttachmentContentType.fromMimeType(stringOrNull(data, "contentType")),
                sizeBytes = intOrDefault(data, "sizeBytes", 0),
                objectPath = stringOrNull(data, "objectPath"),
                downloadUrl = downloadUrl,
                expiresAt = stringOrNull(data, "expiresAt"),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.getAssignmentAttachmentUrl failed", e)
        AssignmentAttachmentTicket(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    // ---- 게시판 ----

    override suspend fun createPost(request: CreatePostRequest): PostResult = try {
        val response = httpsCallable(functions, callableName("createPost"))(
            json(
                "academyId" to request.academyId,
                "title" to request.title,
                "content" to request.content,
            ),
        ).await()
        val data = response.data
        val postId = stringOrNull(data, "postId")
        if (postId == null) {
            PostResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            PostResult(
                post = PostData(
                    id = postId,
                    academyId = stringOrNull(data, "academyId") ?: request.academyId,
                    title = stringOrNull(data, "title") ?: request.title,
                    content = stringOrNull(data, "content") ?: request.content,
                    status = stringOrNull(data, "status").toContentStatus(),
                    authorUid = stringOrNull(data, "authorUid"),
                    authorName = stringOrNull(data, "authorName"),
                    commentCount = intOrDefault(data, "commentCount", 0),
                    // 갓 만든 글에는 첨부가 없다 — 예약은 createPostAttachmentUploadUrl이 따로 한다.
                    createdAt = stringOrNull(data, "createdAt") ?: "",
                ),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.createPost failed", e)
        PostResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun updatePost(request: UpdatePostRequest): CommandResult {
        val payload = json("academyId" to request.academyId, "postId" to request.postId)
        request.title?.let { payload["title"] = it }
        request.content?.let { payload["content"] = it }
        return invokeCommand("updatePost", payload) { stringOrNull(it, "postId") }
    }

    override suspend fun deletePost(academyId: String, postId: String): CommandResult = invokeCommand(
        "deletePost",
        json("academyId" to academyId, "postId" to postId),
    ) { stringOrNull(it, "postId") }

    override suspend fun createComment(request: CreateCommentRequest): CommentResult = try {
        val response = httpsCallable(functions, callableName("createComment"))(
            json(
                "academyId" to request.academyId,
                "postId" to request.postId,
                "content" to request.content,
            ),
        ).await()
        val data = response.data
        val commentId = stringOrNull(data, "commentId")
        if (commentId == null) {
            CommentResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            CommentResult(
                comment = PostComment(
                    id = commentId,
                    postId = stringOrNull(data, "postId") ?: request.postId,
                    academyId = stringOrNull(data, "academyId") ?: request.academyId,
                    content = stringOrNull(data, "content") ?: request.content,
                    status = stringOrNull(data, "status").toContentStatus(),
                    authorUid = stringOrNull(data, "authorUid"),
                    authorName = stringOrNull(data, "authorName"),
                    createdAt = stringOrNull(data, "createdAt") ?: "",
                ),
                notified = pushDeliverySummaryOrNull(data, "notified"),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.createComment failed", e)
        CommentResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun deleteComment(
        academyId: String,
        postId: String,
        commentId: String,
    ): CommandResult = invokeCommand(
        "deleteComment",
        json("academyId" to academyId, "postId" to postId, "commentId" to commentId),
    ) { stringOrNull(it, "commentId") }

    /**
     * 티켓만 받아 온다 — **실제 PUT은 호출부의 몫이다**(Storage SDK로는 올릴 수 없다,
     * `AcademyCommandDataSource.createPostAttachmentUploadUrl` KDoc 참고).
     */
    override suspend fun createPostAttachmentUploadUrl(
        request: CreatePostAttachmentUploadRequest,
    ): PostAttachmentUploadTicket = try {
        val response = httpsCallable(functions, callableName("createPostAttachmentUploadUrl"))(
            json(
                "academyId" to request.academyId,
                "postId" to request.postId,
                "fileName" to request.fileName,
                "contentType" to request.contentType.mimeType,
                "sizeBytes" to request.sizeBytes,
            ),
        ).await()
        val data = response.data
        val uploadUrl = stringOrNull(data, "uploadUrl")
        if (uploadUrl == null) {
            PostAttachmentUploadTicket(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            PostAttachmentUploadTicket(
                attachmentId = stringOrNull(data, "attachmentId"),
                academyId = stringOrNull(data, "academyId") ?: request.academyId,
                postId = stringOrNull(data, "postId") ?: request.postId,
                fileName = stringOrNull(data, "fileName") ?: request.fileName,
                contentType = AttachmentContentType.fromMimeType(stringOrNull(data, "contentType")),
                sizeBytes = intOrDefault(data, "sizeBytes", request.sizeBytes),
                objectPath = stringOrNull(data, "objectPath"),
                uploadUrl = uploadUrl,
                requiredHeaders = stringMapOrEmpty(data, "requiredHeaders"),
                expiresAt = stringOrNull(data, "expiresAt"),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.createPostAttachmentUploadUrl failed", e)
        PostAttachmentUploadTicket(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun getPostAttachmentUrl(
        academyId: String,
        postId: String,
        attachmentId: String,
    ): PostAttachmentDownloadTicket = try {
        val response = httpsCallable(functions, callableName("getPostAttachmentUrl"))(
            json("academyId" to academyId, "postId" to postId, "attachmentId" to attachmentId),
        ).await()
        val data = response.data
        val downloadUrl = stringOrNull(data, "downloadUrl")
        if (downloadUrl == null) {
            PostAttachmentDownloadTicket(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            PostAttachmentDownloadTicket(
                attachmentId = stringOrNull(data, "attachmentId") ?: attachmentId,
                academyId = stringOrNull(data, "academyId") ?: academyId,
                postId = stringOrNull(data, "postId") ?: postId,
                fileName = stringOrNull(data, "fileName"),
                contentType = AttachmentContentType.fromMimeType(stringOrNull(data, "contentType")),
                sizeBytes = intOrDefault(data, "sizeBytes", 0),
                objectPath = stringOrNull(data, "objectPath"),
                downloadUrl = downloadUrl,
                expiresAt = stringOrNull(data, "expiresAt"),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.getPostAttachmentUrl failed", e)
        PostAttachmentDownloadTicket(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun reportContent(request: ContentReportRequest): ContentReportResult {
        val callable = when (request.targetType) {
            ContentReportTargetType.POST -> "reportPost"
            ContentReportTargetType.COMMENT -> "reportComment"
            ContentReportTargetType.USER -> "reportUser"
        }
        return try {
            val payload = json(
                "academyId" to request.academyId,
                "reason" to request.reason.name,
            )
            request.detail?.takeIf { it.isNotBlank() }?.let { payload["detail"] = it }
            when (request.targetType) {
                ContentReportTargetType.POST -> payload["postId"] = request.targetId
                ContentReportTargetType.COMMENT -> {
                    payload["postId"] = request.postId
                    payload["commentId"] = request.targetId
                }
                ContentReportTargetType.USER -> payload["userId"] = request.targetId
            }
            val data = httpsCallable(functions, callableName(callable))(payload).await().data
            ContentReportResult(
                reportId = stringOrNull(data, "reportId"),
                status = ContentReportStatus.entries.firstOrNull { it.name == stringOrNull(data, "status") }
                    ?: ContentReportStatus.OPEN,
                alreadyReported = boolOrDefault(data, "alreadyReported", false),
                reopened = boolOrDefault(data, "reopened", false),
            )
        } catch (e: Throwable) {
            console.error("[EduBridge] callable.$callable failed", e)
            ContentReportResult(
                failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
                errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
            )
        }
    }

    override suspend fun resolveContentReport(
        academyId: String,
        reportId: String,
        resolution: ContentReportResolution,
        note: String?,
    ): CommandResult {
        val payload = json(
            "academyId" to academyId,
            "reportId" to reportId,
            "resolution" to resolution.name,
        )
        note?.takeIf { it.isNotBlank() }?.let { payload["note"] = it }
        return invokeCommand("resolveContentReport", payload) { stringOrNull(it, "reportId") }
    }

    // ---- 일정 ----

    override suspend fun createAcademyEvent(
        request: CreateAcademyEventRequest,
    ): CalendarEventResult = createEvent(
        academyId = request.academyId,
        title = request.title,
        description = request.description,
        startAt = request.startAt,
        endAt = request.endAt,
        allDay = request.allDay,
    )

    /** 서버는 `undefined`인 필드만 "변경 없음"으로 본다 — null을 보내면 `invalid-argument`다. */
    override suspend fun updateAcademyEvent(request: UpdateAcademyEventRequest): CommandResult {
        val payload = json("academyId" to request.academyId, "eventId" to request.eventId)
        applyEventPatch(
            payload,
            request.title,
            request.description,
            request.startAt,
            request.endAt,
            request.allDay,
        )
        return invokeCommand("updateAcademyEvent", payload) { stringOrNull(it, "eventId") }
    }

    override suspend fun deleteAcademyEvent(academyId: String, eventId: String): CommandResult = invokeCommand(
        "deleteAcademyEvent",
        json("academyId" to academyId, "eventId" to eventId),
    ) { stringOrNull(it, "eventId") }

    override suspend fun createPersonalEvent(
        request: CreatePersonalEventRequest,
    ): CalendarEventResult = createEvent(
        academyId = null,
        title = request.title,
        description = request.description,
        startAt = request.startAt,
        endAt = request.endAt,
        allDay = request.allDay,
    )

    /** 대상 문서를 서버가 호출자 uid로 찾으므로 `academyId`도 uid도 보내지 않는다. */
    override suspend fun updatePersonalEvent(request: UpdatePersonalEventRequest): CommandResult {
        val payload = json("eventId" to request.eventId)
        applyEventPatch(
            payload,
            request.title,
            request.description,
            request.startAt,
            request.endAt,
            request.allDay,
        )
        return invokeCommand("updatePersonalEvent", payload) { stringOrNull(it, "eventId") }
    }

    override suspend fun deletePersonalEvent(eventId: String): CommandResult = invokeCommand(
        "deletePersonalEvent",
        json("eventId" to eventId),
    ) { stringOrNull(it, "eventId") }

    // ---- W7 참여 코드 + 가입 신청 (`join.ts`) ----

    override suspend fun issueAcademyJoinCode(academyId: String): JoinCodeResult =
        joinCodeCall("issueAcademyJoinCode", academyId)

    override suspend fun rotateAcademyJoinCode(academyId: String): JoinCodeResult =
        joinCodeCall("rotateAcademyJoinCode", academyId)

    /** 정규화(대문자화·구분자 제거)는 서버 몫이다 — 입력을 그대로 싣는다. */
    override suspend fun previewAcademyJoinCode(joinCode: String): JoinCodePreviewResult = try {
        val response = httpsCallable(functions, callableName("previewAcademyJoinCode"))(
            json("joinCode" to joinCode),
        ).await()
        val data = response.data
        val status = JoinCodePreviewStatus.entries.firstOrNull {
            it.name == stringOrNull(data, "status")
        } ?: JoinCodePreviewStatus.UNUSABLE
        JoinCodePreviewResult(
            status = status,
            // UNUSABLE 응답에는 서버가 대상 정보를 싣지 않는다(코드 열거 방지).
            academyId = if (status == JoinCodePreviewStatus.USABLE) stringOrNull(data, "academyId") else null,
            academyName = if (status == JoinCodePreviewStatus.USABLE) stringOrNull(data, "academyName") else null,
        )
    } catch (e: Throwable) {
        // 참여 코드도 bearer 값이다 — 초대 프리뷰와 같은 이유로 에러 코드만 콘솔에 남긴다.
        console.error("[EduBridge] callable.previewAcademyJoinCode failed", e.jsErrorCode())
        JoinCodePreviewResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun submitAcademyJoinApplication(
        joinCode: String,
        consentAgreed: Boolean,
    ): SubmitJoinApplicationResult = try {
        val response = httpsCallable(functions, callableName("submitAcademyJoinApplication"))(
            json("joinCode" to joinCode, "consentAgreed" to consentAgreed),
        ).await()
        val data = response.data
        SubmitJoinApplicationResult(
            academyId = stringOrNull(data, "academyId"),
            academyName = stringOrNull(data, "academyName"),
            status = stringOrNull(data, "status")?.let { raw ->
                JoinApplicationStatus.entries.firstOrNull { it.name == raw }
            },
            replayed = boolOrDefault(data, "replayed", false),
            attemptCount = intOrDefault(data, "attemptCount", 0),
        )
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.submitAcademyJoinApplication failed", e.jsErrorCode())
        SubmitJoinApplicationResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    override suspend fun cancelAcademyJoinApplication(academyId: String): CommandResult = invokeCommand(
        "cancelAcademyJoinApplication",
        json("academyId" to academyId),
    ) { stringOrNull(it, "academyId") }

    /** 성공 시 entityId는 연결·생성된 학생 문서 id다(계약). [studentId]는 null이면 싣지 않는다. */
    override suspend fun approveAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
        studentId: String?,
    ): CommandResult {
        val payload = json("academyId" to academyId, "applicantUid" to applicantUid)
        studentId?.let { payload["studentId"] = it }
        return invokeCommand("approveAcademyJoinApplication", payload) { stringOrNull(it, "studentId") }
    }

    override suspend fun rejectAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
        blockFuture: Boolean,
    ): CommandResult = invokeCommand(
        "rejectAcademyJoinApplication",
        json("academyId" to academyId, "applicantUid" to applicantUid, "blockFuture" to blockFuture),
    ) { stringOrNull(it, "applicantUid") }

    override suspend fun unblockAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
    ): CommandResult = invokeCommand(
        "unblockAcademyJoinApplication",
        json("academyId" to academyId, "applicantUid" to applicantUid),
    ) { stringOrNull(it, "applicantUid") }

    /**
     * 발급·회전 공통부. 응답 형태가 같고(`code`/`academyId`/`academyName`/`createdAt`),
     * 발급만 `replayed`, 회전만 `revoked`를 실어 온다 — 없는 필드는 기본값으로 접힌다.
     */
    private suspend fun joinCodeCall(name: String, academyId: String): JoinCodeResult = try {
        val response = httpsCallable(functions, callableName(name))(
            json("academyId" to academyId),
        ).await()
        val data = response.data
        val code = stringOrNull(data, "code")
        if (code == null) {
            JoinCodeResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
        } else {
            JoinCodeResult(
                code = code,
                academyId = stringOrNull(data, "academyId") ?: academyId,
                academyName = stringOrNull(data, "academyName"),
                createdAt = stringOrNull(data, "createdAt"),
                replayed = boolOrDefault(data, "replayed", false),
                revokedCount = intOrDefault(data, "revoked", 0),
            )
        }
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.$name failed", e.jsErrorCode())
        JoinCodeResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    // ---- 알림 ----

    override suspend fun notificationSummary(academyId: String): NotificationSummary = try {
        // 리뷰 B6: 콜러블 이름·응답 필드명이 서버 실제 계약과 어긋나 있었다(추정 이름/필드였다 —
        // 클래스 KDoc 함정 3 참고). 서버는 delivered/retried/failed(+jobs·total, 이 DTO에는 대응
        // 슬롯이 없어 매핑하지 않는다)로 응답한다.
        val response = httpsCallable(functions, callableName("getNotificationSummary"))(
            json("academyId" to academyId),
        ).await()
        val data = response.data
        NotificationSummary(
            academyId = academyId,
            deliveredCount = intOrDefault(data, "delivered", 0),
            retryingCount = intOrDefault(data, "retried", 0),
            failedCount = intOrDefault(data, "failed", 0),
        )
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.getNotificationSummary failed", e)
        NotificationSummary(
            academyId = academyId,
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    // ---- 공용 ----

    private fun callableName(name: String): String = "${FirebaseNaming.FUNCTIONS_PREFIX}$name"

    /** `CommandResult`를 돌려주는 대다수 콜러블의 호출 + 성공/실패 매핑 공통부. */
    private suspend fun invokeCommand(
        name: String,
        payload: dynamic,
        entityId: (dynamic) -> String?,
    ): CommandResult = try {
        val response = httpsCallable(functions, callableName(name))(payload).await()
        CommandResult(entityId = entityId(response.data))
    } catch (e: Throwable) {
        console.error("[EduBridge] callable.$name failed", e)
        CommandResult(
            failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
            errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
        )
    }

    /**
     * [AmendAttendanceRequest.previousEventId](이벤트 id) -> `attendanceRecordId`(레코드 id) 변환.
     * 이벤트 문서 자체가 자신이 접힌 레코드의 id를 `recordId` 필드로 갖고 있다
     * (`functions/src/attendance.ts`의 `applyAttendance`가 이벤트 생성 시 함께 기록).
     */
    private suspend fun resolveAttendanceRecordId(academyId: String, eventId: String): String? = try {
        val snapshot = getDoc(
            doc(
                firestore,
                FirebaseNaming.ACADEMIES,
                academyId,
                AcademySubcollections.ATTENDANCE_EVENTS,
                eventId,
            ),
        ).await()
        if (snapshot.exists()) stringOrNull(snapshot.data(), "recordId") else null
    } catch (e: Throwable) {
        console.error("[EduBridge] read.attendanceRecordId failed", e)
        null
    }

    /**
     * 두 등록 콜러블의 공통부. [academyId]가 null이면 개인 일정이며, 그것이 콜러블 이름과
     * [CalendarEvent.scope]와 payload의 `academyId` 포함 여부를 한꺼번에 가른다 — 개인 일정에
     * 학원이 없다는 사실이 세 곳에 따로 적히지 않게 한다.
     */
    private suspend fun createEvent(
        academyId: String?,
        title: String,
        description: String,
        startAt: String,
        endAt: String,
        allDay: Boolean,
    ): CalendarEventResult {
        val name = if (academyId == null) "createPersonalEvent" else "createAcademyEvent"
        return try {
            val payload = json(
                "title" to title,
                "description" to description,
                "startAt" to startAt,
                "endAt" to endAt,
                "allDay" to allDay,
            )
            if (academyId != null) payload["academyId"] = academyId
            val response = httpsCallable(functions, callableName(name))(payload).await()
            val data = response.data
            val eventId = stringOrNull(data, "eventId")
            if (eventId == null) {
                CalendarEventResult(failure = CommandFailure.INTERNAL, errorMessage = GENERIC_COMMAND_ERROR_MESSAGE)
            } else {
                CalendarEventResult(
                    event = CalendarEvent(
                        id = eventId,
                        scope = if (academyId == null) EventScope.PERSONAL else EventScope.ACADEMY,
                        academyId = stringOrNull(data, "academyId") ?: academyId,
                        title = stringOrNull(data, "title") ?: title,
                        description = stringOrNull(data, "description") ?: description,
                        // 서버가 UTC 정규형으로 다시 직렬화해 돌려주므로 요청값이 아니라 응답값이
                        // 정본이다 — 폴백은 응답이 비어 있을 때만 쓴다.
                        startAt = stringOrNull(data, "startAt") ?: startAt,
                        endAt = stringOrNull(data, "endAt") ?: endAt,
                        allDay = boolOrDefault(data, "allDay", allDay),
                        status = stringOrNull(data, "status").toContentStatus(),
                        createdBy = stringOrNull(data, "createdBy"),
                        createdByName = stringOrNull(data, "createdByName"),
                        createdAt = stringOrNull(data, "createdAt") ?: "",
                    ),
                )
            }
        } catch (e: Throwable) {
            console.error("[EduBridge] callable.$name failed", e)
            CalendarEventResult(
                failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()),
                errorMessage = e.jsErrorMessageOrDefault(GENERIC_COMMAND_ERROR_MESSAGE),
            )
        }
    }

    /** 두 수정 콜러블의 공통부. null인 항목은 아예 싣지 않는다 — 서버는 부재만을 "변경 없음"으로 본다. */
    private fun applyEventPatch(
        payload: dynamic,
        title: String?,
        description: String?,
        startAt: String?,
        endAt: String?,
        allDay: Boolean?,
    ) {
        title?.let { payload["title"] = it }
        description?.let { payload["description"] = it }
        startAt?.let { payload["startAt"] = it }
        endAt?.let { payload["endAt"] = it }
        allDay?.let { payload["allDay"] = it }
    }

    private fun scheduleJson(schedule: ClassSchedule): dynamic = json(
        "daysOfWeek" to schedule.daysOfWeek.toTypedArray(),
        "startMinuteOfDay" to schedule.startMinuteOfDay,
        "endMinuteOfDay" to schedule.endMinuteOfDay,
    )

    private fun guardianContactsJson(contacts: List<GuardianContact>): Array<dynamic> =
        contacts.map { json("name" to it.name, "phone" to it.phone, "relation" to it.relation) }.toTypedArray()

    private fun String?.toInviteeTypeOrNull(): InviteeType? =
        InviteeType.entries.firstOrNull { it.name == this }

    /** 생성 콜러블은 항상 `"ACTIVE"`를 돌려주므로 인식 못 하는 값도 ACTIVE로 둔다. */
    private fun String?.toContentStatus(): ContentStatus =
        ContentStatus.entries.firstOrNull { it.name == this } ?: ContentStatus.ACTIVE

    /**
     * `scanAttendance` 실패 사유 매핑. **S3 확정 계약(lane A)**: 거부는 `HttpsError.details.rejection`에
     * EXPIRED/NOT_ENROLLED/NO_STUDENT_RECORD/COOLDOWN/SESSION_ENDED/SESSION_NOT_FOUND 등으로 실려
     * 온다 — 이걸 우선 사용한다. `details`가 없거나(예: 인증 자체가 안 된 경우) 인식 못 하는 값이면
     * 거친 코드 기반 추정으로 폴백한다(EXPIRED/COOLDOWN 모두 `failed-precondition`이라 코드만으로는
     * 구분 못 하고 EXPIRED로 수렴 — details가 있을 때는 이 폴백을 안 타므로 문제되지 않는다).
     *
     * 리뷰 B7: `SESSION_NOT_FOUND`·`NO_STUDENT_RECORD`가 `shared`의 [ScanRejection]에 도착해
     * (서버 레인 선행 작업 완료 확인) 더 이상 접지 않고 직접 매핑한다 — 이전에는 대응 값이 없어
     * `NOT_ENROLLED`로 접어야 했다.
     */
    private fun scanRejectionFor(e: Throwable): ScanRejection {
        val details = e.asDynamic().details
        if (jsTypeOf(details) == "object" && details != null) {
            when (stringOrNull(details, "rejection")) {
                "EXPIRED" -> return ScanRejection.EXPIRED
                "NOT_ENROLLED" -> return ScanRejection.NOT_ENROLLED
                "NO_STUDENT_RECORD" -> return ScanRejection.NO_STUDENT_RECORD
                "COOLDOWN" -> return ScanRejection.COOLDOWN
                "DUPLICATE" -> return ScanRejection.DUPLICATE
                "SESSION_ENDED" -> return ScanRejection.SESSION_ENDED
                "SESSION_NOT_FOUND" -> return ScanRejection.SESSION_NOT_FOUND
                "UNAUTHENTICATED" -> return ScanRejection.UNAUTHENTICATED
            }
        }
        return when (e.jsErrorCode()?.substringAfterLast('/')) {
            "unauthenticated" -> ScanRejection.UNAUTHENTICATED
            "permission-denied" -> ScanRejection.NOT_ENROLLED
            "already-exists" -> ScanRejection.DUPLICATE
            "failed-precondition" -> ScanRejection.EXPIRED
            "unavailable" -> ScanRejection.NETWORK
            else -> ScanRejection.INTERNAL
        }
    }
}
